import { useQueryClient } from '@tanstack/react-query';
import { err, ok, Result } from 'neverthrow';
import { useCallback } from 'react';

import {
    EikaMessageEvent,
    parseCallback,
    RNToWebMessage,
    useNativeEventListener,
    WebActions,
} from '@eika-infrastruktur/mobilbank-lib';

import { Logger } from '@/domain/app';

import { useSigningContext } from '../context';
import {
    DeeplinkMessageSchema,
    MobilePaymentApprovalMessageSchema,
    MobilePaymentStatus,
    PaymentApprovalError,
    PaymentApprovalMessageSchema,
} from '../models';

type SigningStatusPaymentActions = Extract<
    WebActions,
    'PAYMENT_SIGNING_STATUS_UPDATE' | 'SIGNING_STATUS_UPDATE_PAYMENT' | 'DEEPLINK_RESULT'
>;

const filterCallbackEvent = (
    event: EikaMessageEvent<SigningStatusPaymentActions>,
): RNToWebMessage<SigningStatusPaymentActions> | undefined => {
    const callbackData = parseCallback<SigningStatusPaymentActions>(event.data);
    if (callbackData && callbackData.action) {
        Logger.info('signing', 'received event', 'useSigningResultListener', {
            callbackData,
            data: event.data,
        });

        if (
            ['PAYMENT_SIGNING_STATUS_UPDATE', 'SIGNING_STATUS_UPDATE_PAYMENT', 'DEEPLINK_RESULT'].includes(
                callbackData.action,
            )
        ) {
            return callbackData;
        }
    }
    return undefined;
};

const parseSigningEvent = (
    signingEvent: RNToWebMessage<SigningStatusPaymentActions>,
): Result<[MobilePaymentStatus, PaymentApprovalError | undefined], 'PARSE_ERROR'> => {
    switch (signingEvent.action) {
        // STEP UP
        case 'DEEPLINK_RESULT': {
            const { success, data, error } = DeeplinkMessageSchema.safeParse(signingEvent);

            if (success) {
                return ok([data.payload, undefined]);
            }

            if (error) {
                Logger.error('signing', 'DeeplinkPayloadSchema', 'useSigningResultListener', {
                    signingEvent,
                    error,
                });
            }

            return err('PARSE_ERROR');
        }
        // MOBILE
        case 'SIGNING_STATUS_UPDATE_PAYMENT': {
            const { success, data, error } = MobilePaymentApprovalMessageSchema.safeParse(signingEvent);

            if (success) {
                return ok([data.payload, undefined]);
            }

            if (error) {
                Logger.error('signing', 'MobilePaymentApprovalMessageSchema', 'useSigningResultListener', {
                    signingEvent,
                    error,
                });
            }

            return err('PARSE_ERROR');
        }
        // @ts-expect-error this enum is not defined in RNToWebMessage
        case PaymentApprovalAction.PAYMENT_SIGNING_STATUS_UPDATE: {
            const { success, data, error } = PaymentApprovalMessageSchema.safeParse(signingEvent);

            if (success) {
                return ok([data.status, data.approvalError ?? undefined]);
            }

            if (error) {
                Logger.error('signing', 'Invalid callback structure', 'useSigningResultListener', {
                    signingEvent,
                    error,
                });
            }
            return err('PARSE_ERROR');
        }
        default:
            break;
    }
    return err('PARSE_ERROR');
};

export const useSigningResultListener = () => {
    const { setStatus, setError } = useSigningContext();
    const queryClient = useQueryClient();

    const onMessage = useCallback(
        (event: EikaMessageEvent<SigningStatusPaymentActions>) => {
            const signingEvent = filterCallbackEvent(event);
            if (!signingEvent) {
                return;
            }

            parseSigningEvent(signingEvent)
                .andTee(() => {
                    queryClient.invalidateQueries({ queryKey: ['v4'] });
                })
                .match(
                    ([status, error]) => {
                        setStatus(status);
                        setError(error);
                    },
                    () => {
                        setError('INTERNAL_ERROR');
                    },
                );
        },
        [queryClient, setError, setStatus],
    );

    useNativeEventListener(onMessage);
};
